import { createContext, Dispatch, FC, ReactNode, useContext, useEffect, useReducer, useRef } from "react";
import { ExportFormatType, ExportGapFilterType, NetworkProviderFilterType, ExportProviderAvailabilityType, ReportFormatType } from "@app/exports/enumerations";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { useLocation, useRouteMatch } from "react-router-dom";
import { ColumnConfigurationPresetOption } from "@app/report-customization/build-reports/interfaces/columnConfigurationPresetOption";
import { ReportCustomizationRunConfiguration } from "@app/report-customization/build-reports/interfaces/reportCustomizationProjectRunConfiguration";
import { ReportCustomizationRouteState, ReportMembership } from "@app/report-customization/build-reports/interfaces/reportCustomizationRouteState";
import { toast } from "react-toastify";
import { formatDateTime } from "@app/shared/formatDateTime";
import startCase from "lodash/startCase";
import ExportService from "@app/exports/exportService";
import { ReportCustomizationState } from "@app/report-customization/build-reports/interfaces/reportCustomizationState";
import { reportCustomizationReducer } from "@app/report-customization/build-reports/reportCustomizationReducer";
import { ReportCustomizationReducerAction } from "@app/report-customization/build-reports/interfaces/reportCustomizationReducerAction";
import { ReportCustomizationReducerActions } from "@app/report-customization/build-reports/interfaces/reportCustomizationReducerActionsEnum";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import NetNewServicingProviderService from "@app/projects/servicing-provider/NetNewServicingProviderService";
import { defaultAllColumns } from "@app/report-customization/build-reports/defaultPresetValue";
import { ReportCustomizationRunConfigurationNamesEnum } from "@app/report-customization/build-reports/interfaces/reportCustomizationRunConfigurationNamesEnum";
import { ServiceAreaNestingParentDropdownItem } from "@app/report-customization/manage-presets/interfaces/serviceAreaNestingParentDropdownItem";
import { ToAdequacyServicingProvider } from "@app/report-customization/build-reports/interfaces/actions/change-report-type/toAdequacyServicingProvider";
import { ToSpecialtySummaryByCounty } from "@app/report-customization/build-reports/interfaces/actions/change-report-type/toSpecialtySummaryByCounty";
import { ToZipSummary } from "@app/report-customization/build-reports/interfaces/actions/change-report-type/toZipSummary";
import { ToStateSummary } from "@app/report-customization/build-reports/interfaces/actions/change-report-type/toStateSummary";
import { ToCountySummary } from "@app/report-customization/build-reports/interfaces/actions/change-report-type/toCountySummary";
import { ToRegionSummary } from "@app/report-customization/build-reports/interfaces/actions/change-report-type/toRegionSummary";
import { ToAdequacySpecialtySummary } from "@app/report-customization/build-reports/interfaces/actions/change-report-type/toAdequacySpecialtySummary";
import { ToClosestProvidersToMembers } from "@app/report-customization/build-reports/interfaces/actions/change-report-type/toClosestProvidersToMembers";
import { ToZipClassAggregateResults } from "@app/report-customization/build-reports/interfaces/actions/change-report-type/toZipClassAggregateResults";
import { ToAdequacyAggregate } from "@app/report-customization/build-reports/interfaces/actions/change-report-type/toAdequacyAggregate";
import { ToZipAnalysisResults } from "@app/report-customization/build-reports/interfaces/actions/change-report-type/toZipAnalysisResults";
import { ChangeFileFormatSideEffects } from "@app/report-customization/build-reports/interfaces/actions/side-effects/changeFileFormatSideEffects";
import { ChangeReportFormatSideEffects } from "@app/report-customization/build-reports/interfaces/actions/side-effects/changeReportFormatSideEffects";
import { ChangeIsCompareModeSideEffects } from "@app/report-customization/build-reports/interfaces/actions/side-effects/changeIsCompareModeSideEffects";
import { ChangeMembershipsSideEffects } from "@app/report-customization/build-reports/interfaces/actions/side-effects/changeMembershipsSideEffects";
import { ChangeProviderSourceSideEffects } from "@app/report-customization/build-reports/interfaces/actions/side-effects/changeProviderSourceSideEffects";
import { ChangeTimesliceSideEffects } from "@app/report-customization/build-reports/interfaces/actions/side-effects/changeTimesliceSideEffects";
import { changeRunFrequencySideEffects } from "@app/report-customization/build-reports/interfaces/actions/side-effects/changeRunFrequencySideEffects";
import { ProviderSectionDefaults } from "@app/report-customization/build-reports/interfaces/actions/side-effects/providerSectionDefaults";
import { SpecialtySectionDefaults } from "@app/report-customization/build-reports/interfaces/actions/side-effects/specialtySectionDefaults";
import { ServiceAreaSectionDefaults } from "@app/report-customization/build-reports/interfaces/actions/side-effects/serviceAreaSectionDefaults";
import { LoadPageAction } from "@app/report-customization/build-reports/interfaces/actions/loadPageAction";
import { ResetCompareModeSideEffects } from "@app/report-customization/build-reports/interfaces/actions/side-effects/resetCompareModeSideEffects";
import { ToDefault } from "@app/report-customization/build-reports/interfaces/actions/change-report-type/toDefault";
import { ChangedGapsEnum, FilterType, LocationConfidenceScoresLabelEnum, NetworkAssessmentAnalysisType, TimeFrequency } from "@app/projects/enumerators";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { CareJourneyScoresValueEnum, EffectivenessEfficiencyValueEnum, MotiveScoreValueEnum, NewPatientClaimsEnum } from "@app/projects/servicing-provider";
import { ZipClassAnalysisSearchParameters, ZipCodeAnalysisSearchParameters } from "@app/projects/zip-projects/interfaces/zipAnalysisSearchParameters";
import ZipProjectService from "@app/projects/zipProjectService";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import ProjectService from "@app/projects/projectService";
import { getDefaultFileFormat, getDefaultReportName, getDefaultReportNamePrefixByReportType, intersectSpecialties } from "@app/report-customization/build-reports/helpers/helpers";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import { ReportCustomizationRunConfigurationApiResponse } from "@app/report-customization/build-reports/interfaces/reportCustomizationProjectRunConfigurationApiResponse";

interface ReportCustomizationRouteParams {
  isLatestPublished: string;
  module: string;
  projectId: string;
  runConfigurationId: string;
}

interface ReportCustomizationIncomingProps {
  children: ReactNode;
}

export interface ReportCustomizationOutgoingProps {
  changeChangedGapsType: (newChangedGaps: ChangedGapsEnum | "") => void,
  changeFileFormat: (newFileFormat: ExportFormatType) => void,
  changeIsCompareMode: (newIsCompareMode: boolean) => void,
  changeLoadedPresets: () => Promise<void>,
  changeMemberships: (newMemberships: DropdownItem<number>[]) => void,
  changeSelectedPreset: (newSelectedPreset: ColumnConfigurationPresetOption) => void,
  changeProviderSource: (newProviderSource: ProviderNetworkTypeEnum) => void,
  changeRunFrequency: (newRunFrequency: TimeFrequency) => Promise<void>,
  changeReportFormat: (newReportFormat: ReportFormatType) => void,
  changeReportType: (newReportType: ReportType) => void;
  changeTimeslice: (newTimeslice: string, isCompared: boolean) => void;
  dispatch: Dispatch<ReportCustomizationReducerAction>;
  excelRecordsLimit: number,
  findTimeslice: (configId: string | number, runFrequency?: TimeFrequency, refreshes?: ReportCustomizationRunConfiguration[], monthly?: ReportCustomizationRunConfiguration[]) => ReportCustomizationRunConfiguration;
  getReportType: () => ReportType,
  getSelectedRunConfigurationIdsInChronologicalOrder: () => { primaryId: number, secondaryId: number },
  maxExcelRecords: number,
  nonRecordRows: number,
  routeParams: ReportCustomizationRouteParams,
  routeState: ReportCustomizationRouteState,
  state: ReportCustomizationState;
  validateSettings: () => boolean;
}

export const ReportCustomizationContext = createContext<ReportCustomizationOutgoingProps>({} as ReportCustomizationOutgoingProps);

const maxExcelRecords = 1000000; // MS Excel sheet row limit is 1,048,576
const nonRecordRows = 50; // buffer for header, footer
const excelRecordsLimit = maxExcelRecords - nonRecordRows;
type ReportTypeUnion = ToAdequacyAggregate
  | ToAdequacyServicingProvider
  | ToAdequacySpecialtySummary
  | ToClosestProvidersToMembers
  | ToCountySummary
  | ToDefault
  | ToRegionSummary
  | ToSpecialtySummaryByCounty
  | ToStateSummary
  | ToZipAnalysisResults
  | ToZipClassAggregateResults
  | ToZipSummary;

const getInitialMemberships = (primary: ReportMembership, secondary: ReportMembership, selected: number[]) => {
  const selectedModels: DropdownItem<number>[] = [];
  selected?.forEach((s) => {
    if (s === primary.id) {
      selectedModels.push({
        key: primary.id,
        name: primary.alias,
      });
    } else if (s === secondary?.id) {
      selectedModels.push({
        key: secondary.id,
        name: secondary.alias,
      });
    }
  });
  return selectedModels;
}

const getInitialState = (routeState: ReportCustomizationRouteState): ReportCustomizationState => {
  const defaultMemberships = [{ key: routeState.primaryMembership.id, name: routeState.primaryMembership.alias }];
  if (!!routeState.secondaryMembership?.id)
    defaultMemberships.push({ key: routeState.secondaryMembership.id, name: routeState.secondaryMembership.alias });

  const defaultReportType = routeState.defaultReportType || (routeState.isZipProject ? ReportType.ZipAnalysisAdequacyServicingProvider : ReportType.AdequacyServicingProvider);
  const isZipCode = routeState.isZipProject
    && (routeState.defaultReportType === ReportType.ZipAnalysisAdequacyServicingProvider || routeState.defaultReportType === ReportType.ZipAnalysisResultsReport);

  let defaultExportGapType = ExportGapFilterType.All;
  if (!!routeState.zipProjectSearchParameters
    && ((routeState.zipProjectSearchParameters as ZipClassAnalysisSearchParameters).metOverall === false
      || (routeState.zipProjectSearchParameters as ZipCodeAnalysisSearchParameters).metPassPercent === false)) {
    defaultExportGapType = ExportGapFilterType.NotMeetingAccess;
  } else if (!!routeState.adequacyFilters.gaps) {
    defaultExportGapType = ExportGapFilterType.Gaps;
  } else if (!!routeState.adequacyFilters.exceptions) {
    defaultExportGapType = ExportGapFilterType.Exceptions;
  }

  return {
    allMemberships: defaultMemberships,
    allServiceAreaCounties: [],
    allServiceAreaStates: [],
    allSpecialties: [],
    allTimeslices: { all: [], monthly: [] },
    appendNextClosestProviders: false,
    appendPatternOfCareProviders: !!routeState.appendPatternOfCareProviders,
    changedGapsType: routeState.isCompareMode ? (routeState.adequacyFilters?.changedGaps || "") : "",
    comparedTimeslice: routeState.isCompareMode ? (routeState.selectedComparedTimeslice?.toString() || "") : "",
    excludeZeroMembership: routeState.adequacyFilters?.excludeZeroMembership,
    fileFormat: getDefaultFileFormat(routeState.exportNetworkProviderCount > excelRecordsLimit, defaultReportType),
    filename: getDefaultReportName(
      getDefaultReportNamePrefixByReportType(
        (routeState.defaultReportType || (routeState.isZipProject ? ReportType.ZipAnalysisAdequacyServicingProvider : ReportType.AdequacyServicingProvider)),
        routeState.isCompareMode || (routeState.selectedMemberships?.length ?? 0) === 2,
        routeState.defaultProviderSource || ProviderNetworkTypeEnum.Network
      )
    ),
    includeActive: !routeState.isViewingOpportunity,
    includeApproved: !routeState.isViewingOpportunity,
    includeCargo: true,
    includeExpandableSpecialties: false,
    includeFacilitySpecialties: true,
    includeOpportunity: routeState.isViewingOpportunity,
    includePending: !routeState.isViewingOpportunity,
    includePhysicianSpecialties: true,
    isCompareMode: routeState.isCompareMode,
    isDualMembership: (routeState.selectedMemberships?.length ?? 0) > 1,
    isGenerating: false,
    networkAssessmentAnalysisTypes: routeState.isViewingOpportunity ? [NetworkAssessmentAnalysisType.Opportunity] : [NetworkAssessmentAnalysisType.ServiceArea],
    presets: [],
    providerFilter: routeState.defaultProviderFilter || NetworkProviderFilterType.Available,
    providerSource: routeState.defaultProviderSource || ProviderNetworkTypeEnum.Network,
    reportFormat: routeState.appendPatternOfCareProviders ? ReportFormatType.MA : ReportFormatType.Default,
    reportType: defaultReportType,
    runFrequency: routeState.runFrequency || TimeFrequency.All,
    selectedCareJourneyCostValues: Object.values(CareJourneyScoresValueEnum),
    selectedCareJourneyQualityValues: Object.values(CareJourneyScoresValueEnum),
    selectedCounties: routeState.defaultReportType !== ReportType.StateSummary ? routeState.selectedRegionSetIds?.map((rsi) => rsi.toString()) || [] : [],
    selectedEffectivenessValues: Object.values(EffectivenessEfficiencyValueEnum),
    selectedEfficiencyValues: Object.values(EffectivenessEfficiencyValueEnum),
    selectedLineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum.Total,
    selectedLocationConfidenceValues: Object.values(LocationConfidenceScoresLabelEnum),
    selectedMemberships: getInitialMemberships(routeState.primaryMembership, routeState.secondaryMembership, routeState.selectedMemberships ?? []),
    selectedMotiveAppropriatenessValues: Object.values(MotiveScoreValueEnum),
    selectedNewPatientClaimsValues: Object.values(NewPatientClaimsEnum),
    selectedPreset: defaultAllColumns,
    selectedSpecialties: [],
    selectedStates: routeState.defaultReportType === ReportType.StateSummary
      ? routeState.selectedRegionSetIds?.map((rsi) => rsi.toString()) || []
      : isZipCode && !!(routeState.zipProjectSearchParameters as ZipCodeAnalysisSearchParameters)?.state
        ? [(routeState.zipProjectSearchParameters as ZipCodeAnalysisSearchParameters).state]
        : [],
    selectedVolumeLabels: Object.values(ClaimsActivityVolumeLabelEnum),
    selectedZipClass: !!routeState.zipProjectSearchParameters ? routeState.zipProjectSearchParameters.zipClass ?? "all" : "all",
    serviceAreaGapsType: defaultExportGapType,
    sharedUsers: [],
    shouldEmailLink: true,
    showAddedProviders: routeState.showAddedProviders,
    showMatchedProviders: routeState.showMatchedProviders,
    showNetNewProviders: false,
    showRemovedProviders: routeState.showRemovedProviders,
    specialtyGapsType: defaultExportGapType,
    timeslice: routeState.selectedTimeslice.toString(),
  }
}

const ReportCustomizationContextProvider: FC<ReportCustomizationIncomingProps> = (props: ReportCustomizationIncomingProps) => {
  const user = useContext(UserContext);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const routeState = useLocation<ReportCustomizationRouteState>().state;
  const routeMatch = useRouteMatch<ReportCustomizationRouteParams>();
  const projectService = useRef(new ProjectService()).current;
  const zipProjectService = useRef(new ZipProjectService()).current;
  const exportService = useRef(new ExportService()).current;
  const netNewServicingProviderService = useRef(new NetNewServicingProviderService()).current;
  const projectId = parseInt(routeMatch.params.projectId);

  const statesMap = useRef(new Map<string, DropdownItem<string>[]>());
  const countiesMap = useRef(new Map<string, ServiceAreaNestingParentDropdownItem[]>());
  const specialtiesMap = useRef(new Map<string, Array<DropdownItem<string>>>());

  const [state, dispatch] = useReducer(reportCustomizationReducer, routeState, getInitialState);

  const defaultValues = useRef<ReportCustomizationState>({
    allMemberships: state.allMemberships,
    allServiceAreaCounties: state.allServiceAreaCounties,
    allServiceAreaStates: state.allServiceAreaStates,
    allSpecialties: state.allSpecialties,
    allTimeslices: state.allTimeslices,
    appendNextClosestProviders: state.appendNextClosestProviders,
    appendPatternOfCareProviders: state.appendPatternOfCareProviders,
    changedGapsType: "",
    comparedTimeslice: state.comparedTimeslice,
    excludeZeroMembership: state.excludeZeroMembership,
    fileFormat: state.fileFormat,
    filename: state.filename,
    includeActive: state.includeActive,
    includeApproved: state.includeApproved,
    includeCargo: state.includeCargo,
    includeExpandableSpecialties: state.includeExpandableSpecialties,
    includeFacilitySpecialties: state.includeFacilitySpecialties,
    includeOpportunity: state.includeOpportunity,
    includePending: state.includePending,
    includePhysicianSpecialties: state.includePhysicianSpecialties,
    isCompareMode: state.isCompareMode,
    isDualMembership: state.isDualMembership,
    isGenerating: state.isGenerating,
    networkAssessmentAnalysisTypes: state.networkAssessmentAnalysisTypes,
    presets: state.presets,
    providerFilter: state.providerFilter,
    providerSource: state.providerSource,
    reportFormat: state.reportFormat,
    reportType: state.reportType,
    runFrequency: state.runFrequency,
    selectedCareJourneyCostValues: state.selectedCareJourneyCostValues,
    selectedCareJourneyQualityValues: state.selectedCareJourneyQualityValues,
    selectedCounties: state.selectedCounties,
    selectedEffectivenessValues: state.selectedEffectivenessValues,
    selectedEfficiencyValues: state.selectedEfficiencyValues,
    selectedLineOfBusiness: state.selectedLineOfBusiness,
    selectedLocationConfidenceValues: state.selectedLocationConfidenceValues,
    selectedMemberships: state.selectedMemberships,
    selectedMotiveAppropriatenessValues: state.selectedMotiveAppropriatenessValues,
    selectedNewPatientClaimsValues: state.selectedNewPatientClaimsValues,
    selectedPreset: state.selectedPreset,
    selectedSpecialties: state.selectedSpecialties,
    selectedStates: state.selectedStates,
    selectedVolumeLabels: state.selectedVolumeLabels,
    selectedZipClass: state.selectedZipClass,
    serviceAreaGapsType: state.serviceAreaGapsType,
    sharedUsers: state.sharedUsers,
    shouldEmailLink: state.shouldEmailLink,
    showAddedProviders: state.showAddedProviders,
    showMatchedProviders: state.showMatchedProviders,
    showNetNewProviders: state.showNetNewProviders,
    showRemovedProviders: state.showRemovedProviders,
    specialtyGapsType: state.specialtyGapsType,
    timeslice: state.timeslice,
  }).current;

  /**
   * Each report type has a compare mode counterpart, because we save report configuration presets for compare mode
   * separately from non-compare mode. So if we enter or leave compare or dual membership modes, we have to reload the 
   * list of existing preset options.
   */
  useEffect(() => {
    changeLoadedPresets();
  }, [state.isCompareMode, state.isDualMembership]);

  /**
   * Since the Zip Summary report type is available on a per-timeslice basis, there's a case where the timeslice changes
   * and the new timeslice doesn't support that report type. That means we have to change it to something else.
   * Ideally this would be handled in the same event handler as changing the timeslice, but there's a circular reference
   * with the actions and side effects (change timeslice -> change report type -> reset compare mode -> change timeslice...)
   * so this has to be handled separately.
   */
  useEffect(() => {
    const primaryConfig = findTimeslice(state.timeslice);
    const secondaryConfig = findTimeslice(state.timeslice);
    // hard checks for false so undefined timeslices don't cause a false positive before timeslices are loaded
    if (state.reportType === ReportType.ZipSummary && (primaryConfig?.hasZipSummaries === false || secondaryConfig?.hasZipSummaries === false)) {
      changeReportType(defaultValues.reportType === ReportType.ZipSummary ? ReportType.AdequacyServicingProvider : defaultValues.reportType);
    }
  }, [state.timeslice, state.comparedTimeslice, state.allTimeslices]);

  /**
 * The Zip Analysis Adequacy Servicing Provider report with the Next Closest Providers source is specifically designed to show only specialties not meeting access,
 *  so if that report type and provider source are selected together, we have to set the gap types to Not Meeting Access and disable the option to change them until
 *  one of those selections changes. This is required due to the NCP report picking up ALL records, not just the NotMeetingAccess records.
 *
 * TODO: There is incoming work to investigate why the NCP report is pulling all records instead of just the Not Meeting Access records as intended, and once that is resolved,
 *  this special case can be removed.
 *
 * See work item: https://dev.azure.com/questanalytics/Payer%20Products/_workitems/edit/188028
 * Revert code: https://dev.azure.com/questanalytics/Payer%20Products/_git/QES/pullrequest/195877
 */
  useEffect(() => {
    if (state.reportType === ReportType.ZipAnalysisAdequacyServicingProvider && state.providerSource === ProviderNetworkTypeEnum.NextClosestProviders) {
      dispatch({
        payload: {
          serviceAreaGapsType: ExportGapFilterType.NotMeetingAccess,
          specialtyGapsType: ExportGapFilterType.NotMeetingAccess
        },
        type: ReportCustomizationReducerActions.ChangeOnlyShowSpecialtiesNotMeetingAccess,
      });
    }
  }, [state.reportType, state.providerSource]);

  const findTimeslice = (
    configId: string | number,
    runFrequency: TimeFrequency = TimeFrequency.All,
    refreshes: ReportCustomizationRunConfiguration[] = state.allTimeslices.all,
    monthly: ReportCustomizationRunConfiguration[] = state.allTimeslices.monthly
  ) => {
    let id = configId;
    if (typeof id === "string") {
      id = parseInt(id);
    }
    const findFunc = (ts: ReportCustomizationRunConfiguration) => ts.projectRunConfigurationId === id || ts.companionProjectRunConfigurationId === id;
    if (runFrequency === TimeFrequency.All) {
      return refreshes.find(findFunc) || monthly.find(findFunc);
    }
    return monthly.find(findFunc) || refreshes.find(findFunc);
  }

  const changeReportType = async (newReportType: ReportType) => {
    const prevReportType = state.reportType;
    const newFilename = getNewFilename(newReportType);
    const newFileFormat = getDefaultFileFormat(routeState.exportNetworkProviderCount > excelRecordsLimit, newReportType);
    const changeFileFormatSideEffects = getChangeFileFormatSideEffects(newFileFormat);
    const newPresets = await requestPresets(getReportType(newReportType));
    const newSelectedPreset = defaultValues.selectedPreset;

    let newState: Pick<ReportTypeUnion, "fileFormat" | "presets" | "reportType" | "selectedPreset"> & { filename?: string } = {
      fileFormat: newFileFormat,
      ...changeFileFormatSideEffects,
      presets: newPresets,
      reportType: newReportType,
      selectedPreset: newSelectedPreset,
    };

    const serviceAreaRequest = newReportType === ReportType.StateSummary ? requestStates : !routeState.isZipProject ? requestCounties : requestZipCounties;
    const { combinedServiceAreas: newServiceArea, newSelectedServiceAreas } = await serviceAreaRequest();

    switch (newReportType) {
      case ReportType.AdequacyServicingProvider:
      case ReportType.ZipAnalysisAdequacyServicingProvider:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          allServiceAreaCounties: newServiceArea as ServiceAreaNestingParentDropdownItem[],
          changedGapsType: "",
          selectedCounties: newSelectedServiceAreas,
        } as ToAdequacyServicingProvider;
        break;
      case ReportType.SpecialtySummaryByCounty:
      case ReportType.SpecialtySummaryByCountyComparison:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          allServiceAreaCounties: newServiceArea as ServiceAreaNestingParentDropdownItem[],
          selectedCounties: newSelectedServiceAreas,
        } as ToSpecialtySummaryByCounty;
        break;
      case ReportType.ZipSummary:
      case ReportType.ZipSummaryComparison:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          ...getChangeReportFormatSideEffects(defaultValues.reportFormat),
          ...getProviderSectionDefaults(),
          allServiceAreaCounties: newServiceArea as ServiceAreaNestingParentDropdownItem[],
          changedGapsType: "",
          includeCargo: defaultValues.includeCargo,
          selectedCounties: newSelectedServiceAreas,
        } as ToZipSummary;
        break;
      case ReportType.StateSummary:
      case ReportType.StateSummaryComparison:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          ...getChangeReportFormatSideEffects(defaultValues.reportFormat),
          changedGapsType: "",
          includeCargo: defaultValues.includeCargo,
          reportFormat: defaultValues.reportFormat,
          ...getSpecialtySectionDefaults(),
          ...getProviderSectionDefaults(),
          allServiceAreaStates: newServiceArea as DropdownItem<string>[],
          selectedStates: newSelectedServiceAreas,
        } as ToStateSummary;
        break;
      case ReportType.CountySummary:
      case ReportType.CountySummaryComparison:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          ...getChangeReportFormatSideEffects(defaultValues.reportFormat),
          changedGapsType: "",
          includeCargo: defaultValues.includeCargo,
          reportFormat: defaultValues.reportFormat,
          ...getSpecialtySectionDefaults(),
          ...getProviderSectionDefaults(),
          allServiceAreaCounties: newServiceArea as ServiceAreaNestingParentDropdownItem[],
          selectedCounties: newSelectedServiceAreas,
        } as ToCountySummary;
        break;
      case ReportType.RegionSummary:
      case ReportType.RegionSummaryComparison:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          ...getChangeReportFormatSideEffects(defaultValues.reportFormat),
          changedGapsType: "",
          includeCargo: defaultValues.includeCargo,
          reportFormat: defaultValues.reportFormat,
          ...getSpecialtySectionDefaults(),
          ...getProviderSectionDefaults(),
          allServiceAreaCounties: newServiceArea as ServiceAreaNestingParentDropdownItem[],
          selectedCounties: newSelectedServiceAreas,
        } as ToRegionSummary;
        break;
      case ReportType.AdequacySpecialtySummary:
      case ReportType.AdequacySpecialtySummaryComparison:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          ...getChangeReportFormatSideEffects(defaultValues.reportFormat),
          changedGapsType: "",
          includeCargo: defaultValues.includeCargo,
          reportFormat: defaultValues.reportFormat,
          ...getProviderSectionDefaults(),
          allServiceAreaCounties: newServiceArea as ServiceAreaNestingParentDropdownItem[],
          selectedCounties: newSelectedServiceAreas,
        } as ToAdequacySpecialtySummary;
        break;
      case ReportType.AdequacyAggregate:
        const newSelectedCounties = getSelectedCountiesForAdequacyAggregate(
          prevReportType,
          newServiceArea as ServiceAreaNestingParentDropdownItem[],
          newSelectedServiceAreas
        );

        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          ...getChangeReportFormatSideEffects(defaultValues.reportFormat),
          changedGapsType: "",
          includeCargo: defaultValues.includeCargo,
          ...getProviderSectionDefaults(),
          allServiceAreaCounties: newServiceArea as ServiceAreaNestingParentDropdownItem[],
          selectedCounties: newSelectedCounties,
          serviceAreaGapsType: defaultValues.serviceAreaGapsType,
        } as ToAdequacyAggregate;
        break;
      case ReportType.ClosestProvidersToMembers:
        newState = {
          ...newState,
          ...getChangeIsCompareModeSideEffects(false),
          changedGapsType: "",
          isCompareMode: false,
          ...getServiceAreaSectionDefaults(),
          ...getSpecialtySectionDefaults(),
          ...getProviderSectionDefaults(),
        } as ToClosestProvidersToMembers;
        break;
      case ReportType.ZipClassAggregateResultsReport:
        newState = {
          ...newState,
          ...getServiceAreaSectionDefaults(),
          ...getProviderSectionDefaults(),
        } as ToZipClassAggregateResults;
        break;
      case ReportType.ZipAnalysisResultsReport:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          ...getServiceAreaSectionDefaults(),
          ...getSpecialtySectionDefaults(),
          ...getProviderSectionDefaults(),
        } as ToZipAnalysisResults;
      default:
        newState = {
          ...newState,
          ...(await resetCompareMode(prevReportType)),
          ...getServiceAreaSectionDefaults(),
          ...getSpecialtySectionDefaults(),
          ...getProviderSectionDefaults(),
        } as ToDefault;
        break;
    }

    // filename has to be added last so none of the switch cases overwrite it
    // and there's no circumstance where a preset should be retained across report types
    newState = {
      ...newState,
      filename: newFilename,
      selectedPreset: defaultValues.selectedPreset,
    }

    dispatch({
      payload: newState as ReportCustomizationState,
      type: ReportCustomizationReducerActions.ChangeReportType,
    });
  }

  const changeTimeslice = async (newTimeslice: string, isCompared: boolean) => {
    let primaryTimeslice: string;
    let secondaryTimeslice: string;

    if (isCompared) {
      primaryTimeslice = state.timeslice;
      secondaryTimeslice = newTimeslice;
    } else {
      primaryTimeslice = newTimeslice;
      secondaryTimeslice = state.comparedTimeslice;
    }

    dispatch({
      payload: {
        ...(await getChangeTimesliceSideEffects(primaryTimeslice, secondaryTimeslice)),
        comparedTimeslice: secondaryTimeslice,
        timeslice: primaryTimeslice,
      },
      type: isCompared ? ReportCustomizationReducerActions.ChangeComparedTimeslice : ReportCustomizationReducerActions.ChangeTimeslice,
    })
  }

  const getDummyChangeTimesliceSideEffects = (): ChangeTimesliceSideEffects => {
    return {
      allMemberships: state.allMemberships,
      allServiceAreaCounties: state.allServiceAreaCounties,
      allServiceAreaStates: state.allServiceAreaStates,
      allSpecialties: state.allSpecialties,
      appendNextClosestProviders: state.appendNextClosestProviders,
      appendPatternOfCareProviders: state.appendPatternOfCareProviders,
      changedGapsType: state.changedGapsType,
      comparedTimeslice: state.comparedTimeslice,
      filename: state.filename,
      includeActive: state.includeActive,
      includeApproved: state.includeApproved,
      includeFacilitySpecialties: state.includeFacilitySpecialties,
      includeOpportunity: state.includeOpportunity,
      includePending: state.includePending,
      includePhysicianSpecialties: state.includePhysicianSpecialties,
      isCompareMode: state.isCompareMode,
      isDualMembership: state.isDualMembership,
      providerFilter: state.providerFilter,
      providerSource: state.providerSource,
      reportFormat: state.reportFormat,
      selectedCounties: state.selectedCounties,
      selectedMemberships: state.selectedMemberships,
      selectedSpecialties: state.selectedSpecialties,
      selectedStates: state.selectedStates,
      selectedZipClass: state.selectedZipClass,
      serviceAreaGapsType: state.serviceAreaGapsType,
      showAddedProviders: state.showAddedProviders,
      showMatchedProviders: state.showMatchedProviders,
      showRemovedProviders: state.showRemovedProviders,
      specialtyGapsType: state.specialtyGapsType,
    }
  }

  const getChangeTimesliceSideEffects = async (primaryTimeslice: string, secondaryTimeslice: string): Promise<ChangeTimesliceSideEffects> => {
    const primaryConfig = findTimeslice(primaryTimeslice);
    const secondaryConfig = findTimeslice(secondaryTimeslice);
    const newAllMemberships = [{ key: primaryConfig.primaryMemberSetId, name: primaryConfig.primaryMemberSetAlias || primaryConfig.primaryMemberSetName }];
    if (!!primaryConfig.secondaryMemberSetId) {
      newAllMemberships.push({ key: primaryConfig.secondaryMemberSetId, name: primaryConfig.secondaryMemberSetAlias || primaryConfig.secondaryMemberSetName });
    }

    const isStateSummary = state.reportType === ReportType.StateSummary;
    let newAllServiceAreas = isStateSummary ? state.allServiceAreaStates : state.allServiceAreaCounties;
    let allServiceAreaIds: string[] = [];
    let newAllSpecialties = state.allSpecialties;
    // filter the selected service areas and specialties to ones that still exist in the new options
    let newSelectedServiceAreas: string[] = [];

    // for primary timeslice changes, make sure the selected membership is still valid
    let newSelectedMemberships: DropdownItem<number>[] = [];
    let newIsCompareMode = state.isCompareMode;
    let newComparedTimeslice = secondaryTimeslice;
    let newProviderSource = state.providerSource;
    let changeProviderSourceSideEffects: ChangeProviderSourceSideEffects = getDummyChangeProviderSourceSideEffects();
    let changeMembershipsSideEffects: ChangeMembershipsSideEffects = getDummyChangeMembershipsSideEffects();
    let changeIsCompareModeSideEffects: ChangeIsCompareModeSideEffects = getDummyChangeIsCompareModeSideEffects();

    if (primaryTimeslice !== state.timeslice) {
      state.selectedMemberships.forEach((m) => {
        if (m.key === primaryConfig.primaryMemberSetId || m.key === primaryConfig.secondaryMemberSetId) {
          newSelectedMemberships.push(m);
        }
      })
      // if there are no valid selections, make new ones
      if (newSelectedMemberships.length === 0) {
        newSelectedMemberships.push({ key: primaryConfig.primaryMemberSetId, name: primaryConfig.primaryMemberSetAlias || primaryConfig.primaryMemberSetName });
        if (routeState.selectedMemberships.length === 2 && !!primaryConfig.secondaryMemberSetId) {
          newSelectedMemberships.push({ key: primaryConfig.secondaryMemberSetId, name: primaryConfig.secondaryMemberSetAlias || primaryConfig.secondaryMemberSetName },);
        }
        changeMembershipsSideEffects = getChangeMembershipsSideEffects(newSelectedMemberships);
        // since there are new memberships, make sure the secondary timeslice (if any) is still valid
        if (!!secondaryTimeslice) {
          if (!newSelectedMemberships.every((m) => m.key === secondaryConfig.primaryMemberSetId || m.key === secondaryConfig.secondaryMemberSetId)) {
            newIsCompareMode = false;
            changeIsCompareModeSideEffects = getChangeIsCompareModeSideEffects(newIsCompareMode);
            newComparedTimeslice = "";
          }
        }
      } else {
        // if there are valid selections, still make sure the aliases/names are up to date
        // i.e. in case a primary membership is a secondary on a different timeslice
        newSelectedMemberships = newSelectedMemberships.map((m) => primaryConfig.primaryMemberSetId === m.key
          ? { key: m.key, name: primaryConfig.primaryMemberSetAlias || primaryConfig.primaryMemberSetName }
          : { key: m.key, name: primaryConfig.secondaryMemberSetAlias || primaryConfig.secondaryMemberSetName })
      }
    } else {
      newSelectedMemberships = state.selectedMemberships;
    }

    if (primaryConfig.name !== ReportCustomizationRunConfigurationNamesEnum.LatestPublished
      && primaryConfig.name !== ReportCustomizationRunConfigurationNamesEnum.Unpublished
      && primaryConfig.name !== ReportCustomizationRunConfigurationNamesEnum.Projected
      && primaryConfig.name !== ReportCustomizationRunConfigurationNamesEnum.Termed
      && primaryConfig.name !== ReportCustomizationRunConfigurationNamesEnum.ProjectedTermed) {

      newProviderSource = defaultValues.providerSource;
      changeProviderSourceSideEffects = getChangeProviderSourceSideEffects(newProviderSource);
    }

    // If current provider source is not Network, check if it's still valid for the new timeslice
    if (newProviderSource !== ProviderNetworkTypeEnum.Network) {
      let shouldResetToNetwork = false;
      // WI 194524: MA HSD on an RPPO run is restricted to Network providers
      const isRppoNetworkOnly = enabledFeatureFlags.has(Features.RPPO_NON_PAR_PROVIDER_TO_HSD_TABLE_INCLUSION)
        && state.reportFormat === ReportFormatType.MA
        && !!primaryConfig.rppoNonContractedProvidersIncludedInHsd;

      switch (newProviderSource) {
        case ProviderNetworkTypeEnum.Prospect:
          shouldResetToNetwork = !primaryConfig.hasProspectProviders || isRppoNetworkOnly;
          break;
        case ProviderNetworkTypeEnum.Market:
          shouldResetToNetwork = !primaryConfig.hasMarketProviders || isRppoNetworkOnly;
          break;
        case ProviderNetworkTypeEnum.NextClosestProviders:
          shouldResetToNetwork = !primaryConfig.showNextClosestProviders;
          break;
        case ProviderNetworkTypeEnum.NetNew:
          const shouldShowNetNew = primaryConfig && (
            primaryConfig.name === ReportCustomizationRunConfigurationNamesEnum.LatestPublished ||
            primaryConfig.name === ReportCustomizationRunConfigurationNamesEnum.Unpublished ||
            primaryConfig.name === ReportCustomizationRunConfigurationNamesEnum.Projected ||
            primaryConfig.name === ReportCustomizationRunConfigurationNamesEnum.Termed ||
            primaryConfig.name === ReportCustomizationRunConfigurationNamesEnum.ProjectedTermed
          );
          shouldResetToNetwork = !shouldShowNetNew;
          break;
      }

      if (shouldResetToNetwork) {
        newProviderSource = ProviderNetworkTypeEnum.Network;
        changeProviderSourceSideEffects = getChangeProviderSourceSideEffects(ProviderNetworkTypeEnum.Network);
      }
    }

    // retrieve updated service area and specialty options
    // async functions always return a promise, so the typing here is consistent
    // it's just ~instantaneous if it's in the Map already
    const promises: Promise<ServiceAreaNestingParentDropdownItem | DropdownItem<string>[]>[] = [];
    if (isStateSummary) {
      promises.push(statesMap.current.has(primaryTimeslice) ? (async () => statesMap.current.get(primaryTimeslice))() : projectService.getStatesByRunConfigurationId(routeMatch.params.projectId, primaryTimeslice));
    } else if (!routeState.isZipProject) {
      promises.push(countiesMap.current.has(primaryTimeslice) ? (async () => countiesMap.current.get(primaryTimeslice))() : projectService.getCountiesByRunConfigurationId(routeMatch.params.projectId, primaryTimeslice, false, true));
    } else {
      promises.push(countiesMap.current.has(primaryTimeslice) ? (async () => countiesMap.current.get(primaryTimeslice))() : zipProjectService.getZipCodeFilterOptions(projectId, parseInt(primaryTimeslice), true).then((response) => response.countiesAndStates))
    }
    promises.push(specialtiesMap.current.has(primaryTimeslice) ? (async () => specialtiesMap.current.get(primaryTimeslice))() : projectService.getSpecialtiesByRunConfigurationId(routeMatch.params.projectId, primaryTimeslice));

    if (state.isCompareMode && !!secondaryTimeslice) {
      if (isStateSummary) {
        promises.push(statesMap.current.has(secondaryTimeslice) ? (async () => statesMap.current.get(secondaryTimeslice))() : projectService.getStatesByRunConfigurationId(routeMatch.params.projectId, secondaryTimeslice));
      } else if (!routeState.isZipProject) {
        promises.push(countiesMap.current.has(secondaryTimeslice) ? (async () => countiesMap.current.get(secondaryTimeslice))() : projectService.getCountiesByRunConfigurationId(routeMatch.params.projectId, secondaryTimeslice, false, true));
      } else {
        promises.push(countiesMap.current.has(primaryTimeslice) ? (async () => countiesMap.current.get(primaryTimeslice))() : zipProjectService.getZipCodeFilterOptions(projectId, parseInt(secondaryTimeslice), false).then((response) => response.countiesAndStates))
      }
      promises.push(specialtiesMap.current.has(secondaryTimeslice) ? (async () => specialtiesMap.current.get(secondaryTimeslice))() : projectService.getSpecialtiesByRunConfigurationId(routeMatch.params.projectId, secondaryTimeslice));

      const [newPrimaryServiceArea, newPrimarySpecialties, newSecondaryServiceArea, newSecondarySpecialties] = await Promise.all(promises);

      if (isStateSummary) {
        statesMap.current.set(primaryTimeslice, newPrimaryServiceArea as DropdownItem<string>[]);
        statesMap.current.set(secondaryTimeslice, newSecondaryServiceArea as DropdownItem<string>[]);
        newAllServiceAreas = intersectStates(newPrimaryServiceArea as DropdownItem<string>[], newSecondaryServiceArea as DropdownItem<string>[]);
        allServiceAreaIds = newAllServiceAreas.map((csa) => csa.key);

        newSelectedServiceAreas = state.selectedStates.filter((p) => allServiceAreaIds.includes(p));
      } else {
        countiesMap.current.set(primaryTimeslice, newPrimaryServiceArea as ServiceAreaNestingParentDropdownItem[]);
        countiesMap.current.set(secondaryTimeslice, newSecondaryServiceArea as ServiceAreaNestingParentDropdownItem[]);
        newAllServiceAreas = intersectCounties(newPrimaryServiceArea as ServiceAreaNestingParentDropdownItem[], newSecondaryServiceArea as ServiceAreaNestingParentDropdownItem[]);
        allServiceAreaIds = (newAllServiceAreas as ServiceAreaNestingParentDropdownItem[]).flatMap((csa) => csa.children.map((c) => c.key));
        newSelectedServiceAreas = state.selectedCounties.filter((p) => allServiceAreaIds.includes(p));
      }
      specialtiesMap.current.set(primaryTimeslice, newPrimarySpecialties as DropdownItem<string>[]);
      specialtiesMap.current.set(secondaryTimeslice, newSecondarySpecialties as DropdownItem<string>[]);

      newAllSpecialties = intersectSpecialties(newPrimarySpecialties as DropdownItem<string>[], newSecondarySpecialties as DropdownItem<string>[]);
    } else {
      const [newServiceAreas, newSpecialties] = await Promise.all(promises);
      if (isStateSummary) {
        statesMap.current.set(primaryTimeslice, newServiceAreas as DropdownItem<string>[]);
        allServiceAreaIds = (newServiceAreas as DropdownItem<string>[]).map((csa) => csa.key);
        newSelectedServiceAreas = state.selectedStates.filter((p) => allServiceAreaIds.includes(p));
      } else {
        countiesMap.current.set(primaryTimeslice, newServiceAreas as ServiceAreaNestingParentDropdownItem[]);
        allServiceAreaIds = (newServiceAreas as ServiceAreaNestingParentDropdownItem[]).flatMap((csa) => csa.children.map((c) => c.key));
        newSelectedServiceAreas = state.selectedCounties.filter((p) => allServiceAreaIds.includes(p));
      }
      newAllServiceAreas = newServiceAreas as ServiceAreaNestingParentDropdownItem[];
      specialtiesMap.current.set(primaryTimeslice, newSpecialties as DropdownItem<string>[]);
      newAllSpecialties = newSpecialties as DropdownItem<string>[];
    }
    const newSelectedSpecialties = state.selectedSpecialties.filter((p) => !!newAllSpecialties.find((cs) => cs.key === p.key));

    const newSelectedZipClass = primaryConfig.zipClasses?.find((zc) => state.selectedZipClass === zc) || "all";

    return {
      ...changeMembershipsSideEffects,
      ...changeIsCompareModeSideEffects,
      ...changeProviderSourceSideEffects,
      allMemberships: newAllMemberships,
      allServiceAreaCounties: isStateSummary ? state.allServiceAreaCounties : newAllServiceAreas as ServiceAreaNestingParentDropdownItem[],
      allServiceAreaStates: isStateSummary ? newAllServiceAreas as DropdownItem<string>[] : state.allServiceAreaStates,
      allSpecialties: newAllSpecialties,
      changedGapsType: newIsCompareMode ? state.changedGapsType : "",
      comparedTimeslice: newComparedTimeslice,
      providerSource: newProviderSource,
      selectedCounties: isStateSummary ? state.selectedCounties : newSelectedServiceAreas,
      selectedMemberships: newSelectedMemberships,
      selectedSpecialties: newSelectedSpecialties,
      selectedStates: isStateSummary ? newSelectedServiceAreas : state.selectedStates,
      selectedZipClass: newSelectedZipClass,
    };
  }

  const changeFileFormat = (newFileFormat: ExportFormatType) => {
    const changeFileFormatSideEffects = getChangeFileFormatSideEffects(newFileFormat);

    dispatch({
      payload: {
        ...changeFileFormatSideEffects,
        fileFormat: newFileFormat,
      },
      type: ReportCustomizationReducerActions.ChangeFileFormat,
    })
  }

  const getChangeFileFormatSideEffects = (newFileFormat: ExportFormatType): ChangeFileFormatSideEffects => {
    let changeReportFormatSideEffects = getDummyChangeReportFormatSideEffects();
    let newReportFormat = state.reportFormat;
    if (newFileFormat === ExportFormatType.Excel || newFileFormat === ExportFormatType.Pipe) {
      newReportFormat = ReportFormatType.Default;
      changeReportFormatSideEffects = getChangeReportFormatSideEffects(ReportFormatType.Default);
    }
    return {
      ...changeReportFormatSideEffects,
      reportFormat: newReportFormat,
    };
  }

  const changeMemberships = (newMemberships: DropdownItem<number>[]) => {
    const changeMembershipsSideEffects = getChangeMembershipsSideEffects(newMemberships);

    dispatch({
      payload: {
        ...changeMembershipsSideEffects,
        selectedMemberships: newMemberships || [],
      },
      type: ReportCustomizationReducerActions.ChangeMemberships,
    })
  }

  const getDummyChangeMembershipsSideEffects = (): ChangeMembershipsSideEffects => {
    return {
      appendNextClosestProviders: state.appendNextClosestProviders,
      appendPatternOfCareProviders: state.appendPatternOfCareProviders,
      changedGapsType: state.changedGapsType,
      comparedTimeslice: state.comparedTimeslice,
      filename: state.filename,
      includeActive: state.includeActive,
      includeApproved: state.includeApproved,
      includeFacilitySpecialties: state.includeFacilitySpecialties,
      includeOpportunity: state.includeOpportunity,
      includePending: state.includePending,
      includePhysicianSpecialties: state.includePhysicianSpecialties,
      isCompareMode: state.isCompareMode,
      isDualMembership: state.isDualMembership,
      providerFilter: state.providerFilter,
      providerSource: state.providerSource,
      reportFormat: state.reportFormat,
      selectedCounties: state.selectedCounties,
      selectedMemberships: state.selectedMemberships,
      selectedSpecialties: state.selectedSpecialties,
      selectedStates: state.selectedStates,
      serviceAreaGapsType: state.serviceAreaGapsType,
      showAddedProviders: state.showAddedProviders,
      showMatchedProviders: state.showMatchedProviders,
      showRemovedProviders: state.showRemovedProviders,
      specialtyGapsType: state.specialtyGapsType,
    }
  };

  const getChangeMembershipsSideEffects = (newMemberships: DropdownItem<number>[]): ChangeMembershipsSideEffects => {
    const newFilename = getNewFilename(undefined, undefined, newMemberships);
    const isTwoMemberships = newMemberships.length === 2;
    let changeIsCompareModeSideEffects = getDummyChangeIsCompareModeSideEffects();
    let changeProviderSourceSideEffects = getDummyChangeProviderSourceSideEffects();
    let newProviderSource = state.providerSource;
    let newIsCompareMode = state.isCompareMode;
    if (isTwoMemberships) {
      newIsCompareMode = false;
      changeIsCompareModeSideEffects = getChangeIsCompareModeSideEffects(false);
      newProviderSource = ProviderNetworkTypeEnum.Network;
      changeProviderSourceSideEffects = getChangeProviderSourceSideEffects(ProviderNetworkTypeEnum.Network);
    }


    return {
      ...changeIsCompareModeSideEffects,
      ...changeProviderSourceSideEffects,
      filename: newFilename,
      isCompareMode: newIsCompareMode,
      isDualMembership: isTwoMemberships,
      providerSource: newProviderSource,
      showAddedProviders: isTwoMemberships,
      showMatchedProviders: isTwoMemberships,
      showRemovedProviders: isTwoMemberships,
    };
  }

  const changeIsCompareMode = (newIsCompareMode: boolean) => {
    const changeIsCompareModeSideEffects = getChangeIsCompareModeSideEffects(newIsCompareMode);
    dispatch({
      payload: {
        ...changeIsCompareModeSideEffects,
        isCompareMode: newIsCompareMode,
      },
      type: ReportCustomizationReducerActions.ChangeIsCompareMode,
    });
    // hard check false here, because it'll be undefined if newIsCompareMode is false
    if (state.appendPatternOfCareProviders && changeIsCompareModeSideEffects.appendPatternOfCareProviders === false) {
      renderPocUncheckedToast();
    }
  };

  const getDummyChangeIsCompareModeSideEffects = (): ChangeIsCompareModeSideEffects => {
    return {
      appendNextClosestProviders: state.appendNextClosestProviders,
      appendPatternOfCareProviders: state.appendPatternOfCareProviders,
      changedGapsType: state.changedGapsType,
      comparedTimeslice: state.comparedTimeslice,
      filename: state.filename,
      includeActive: state.includeActive,
      includeApproved: state.includeApproved,
      includeFacilitySpecialties: state.includeFacilitySpecialties,
      includeOpportunity: state.includeOpportunity,
      includePending: state.includePending,
      includePhysicianSpecialties: state.includePhysicianSpecialties,
      providerFilter: state.providerFilter,
      providerSource: state.providerSource,
      reportFormat: state.reportFormat,
      selectedCounties: state.selectedCounties,
      selectedMemberships: state.selectedMemberships,
      selectedSpecialties: state.selectedSpecialties,
      selectedStates: state.selectedStates,
      serviceAreaGapsType: state.serviceAreaGapsType,
      showAddedProviders: state.showAddedProviders,
      showMatchedProviders: state.showMatchedProviders,
      showRemovedProviders: state.showRemovedProviders,
      specialtyGapsType: state.specialtyGapsType,
    }
  };

  const getChangeIsCompareModeSideEffects = (newIsCompareMode: boolean): ChangeIsCompareModeSideEffects => {
    let newMemberships = state.selectedMemberships;
    let newComparedTimeslice = state.comparedTimeslice;
    let newChangedGapsType = state.changedGapsType;
    let newProviderSource = state.providerSource;
    const runFrequencyTimeslices = state.allTimeslices[state.runFrequency];
    const primaryConfig = findTimeslice(state.timeslice);
    let providerSourceSideEffects: ChangeProviderSourceSideEffects = getDummyChangeProviderSourceSideEffects();

    if (newIsCompareMode) {
      newMemberships = state.selectedMemberships.length > 0
        ? [state.selectedMemberships[0]]
        : [{
          key: primaryConfig.primaryMemberSetId,
          name: primaryConfig.primaryMemberSetAlias || primaryConfig.primaryMemberSetName,
        }];
      newProviderSource = ProviderNetworkTypeEnum.Network;
      providerSourceSideEffects = getChangeProviderSourceSideEffects(ProviderNetworkTypeEnum.Network);

      // we need the latest published non-projected/termed timeslice that
      // is also not the selected primary timeslice and also shares a membership with the primary timeslice
      const eligibleComparedTimeslices = runFrequencyTimeslices
        .filter((ts) => (
          ts.name !== "Projected"
          && ts.name !== "Termed"
          && ts.name !== "Projected-Termed"
          && ts.name !== "Unpublished"
          && ts.projectRunConfigurationId.toString() !== state.timeslice
          && (newMemberships[0].key === ts.primaryMemberSetId
            || newMemberships[0].key === ts.secondaryMemberSetId
            || ts.secondaryMemberSetId === 0))); // handle edge case where secondary membership is deleted but timeslice still exists

      const latestEligibleTimestamp = Math.max(...eligibleComparedTimeslices.map((ts) => new Date(ts.dateCreated).getTime()));
      newComparedTimeslice = eligibleComparedTimeslices.find((ts) => new Date(ts.dateCreated).getTime() === latestEligibleTimestamp)?.projectRunConfigurationId.toString() || "";
    } else {
      newComparedTimeslice = "";
      newChangedGapsType = "";
    }

    const newFilename = getNewFilename(undefined, newIsCompareMode);

    // Clear appendNextClosestProviders when enabling compare mode
    let newAppendNextClosestProviders = providerSourceSideEffects.appendNextClosestProviders;
    if (newIsCompareMode) {
      newAppendNextClosestProviders = false;
    }

    return {
      ...providerSourceSideEffects,
      appendNextClosestProviders: newAppendNextClosestProviders,
      changedGapsType: newChangedGapsType,
      comparedTimeslice: newComparedTimeslice,
      filename: newFilename,
      providerSource: newProviderSource,
      selectedMemberships: newMemberships,
      showAddedProviders: newIsCompareMode,
      showMatchedProviders: newIsCompareMode,
      showRemovedProviders: newIsCompareMode,
    };
  }

  const changeProviderSource = (newProviderSource: ProviderNetworkTypeEnum) => {
    const sideEffects = getChangeProviderSourceSideEffects(newProviderSource);
    dispatch({
      payload: {
        ...sideEffects,
        providerSource: newProviderSource,
      },
      type: ReportCustomizationReducerActions.ChangeProviderSource,
    });
    if (state.appendPatternOfCareProviders && !sideEffects.appendPatternOfCareProviders) {
      renderPocUncheckedToast();
    }
  }

  const changeRunFrequency = async (newRunFrequency: TimeFrequency) => {
    const sideEffects = await getChangeRunFrequencySideEffects(newRunFrequency);

    dispatch({
      payload: {
        runFrequency: newRunFrequency,
        ...sideEffects,
      },
      type: ReportCustomizationReducerActions.ChangeRunFrequency,
    });
  }

  const getChangeRunFrequencySideEffects = async (newRunFrequency: TimeFrequency): Promise<changeRunFrequencySideEffects> => {
    let newTimeslice = state.timeslice;
    let newComparedTimeslice = state.comparedTimeslice;
    let availableIds: number[] = [];

    if (newRunFrequency === TimeFrequency.Monthly) {
      const { monthly } = state.allTimeslices;
      availableIds = monthly
        .filter(option => !option.isDisabled)
        .map(option => option.projectRunConfigurationId);
    } else {
      availableIds = state.allTimeslices.all.map(t => t.projectRunConfigurationId);
    }

    const getMatchingTimeslice = (timesliceId: string) => {
      const currentTimesliceId = parseInt(timesliceId);
      const isCurrentTimesliceAvailable = availableIds.includes(currentTimesliceId);

      // If current timeslice is not available,
      // check if it's from the same month as any available monthly option
      if (!isCurrentTimesliceAvailable && newRunFrequency === TimeFrequency.Monthly) {
        const currentTimeslice = state.allTimeslices.all.find(t => t.projectRunConfigurationId === currentTimesliceId);
        if (currentTimeslice) {
          const currentDate = new Date(currentTimeslice.dateCreated);
          const currentMonthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

          // Find if there's a monthly option for the same month available
          const { monthly } = state.allTimeslices;
          const sameMonthOption = monthly.find(option =>
            !option.isDisabled && option.monthYear === currentMonthYear
          );

          if (sameMonthOption) {
            return sameMonthOption.projectRunConfigurationId.toString();
          }
        }
      }
      return null;
    }

    // if there's not a matching config in the new run frequency, just keep the old one
    // it'll show in the dropdown as long as it's still selected
    newTimeslice = getMatchingTimeslice(newTimeslice) ?? newTimeslice;
    newComparedTimeslice = getMatchingTimeslice(newComparedTimeslice) ?? newComparedTimeslice;

    // Handle secondary timeslice validation when compare mode is enabled
    if (state.isCompareMode && (!newComparedTimeslice || newComparedTimeslice === newTimeslice)) {
      const currentTimesliceIdToCheck = parseInt(newTimeslice);
      const firstAvailableId = availableIds.find(id => id !== currentTimesliceIdToCheck);
      if (firstAvailableId) {
        newComparedTimeslice = firstAvailableId.toString();
      }
    }

    const changeTimesliceSideEffects = await getChangeTimesliceSideEffects(newTimeslice, newComparedTimeslice);

    return {
      comparedTimeslice: newComparedTimeslice,
      timeslice: newTimeslice,
      ...changeTimesliceSideEffects,
    };
  }

  const getDummyChangeProviderSourceSideEffects = (): ChangeProviderSourceSideEffects => {
    return {
      appendNextClosestProviders: state.appendNextClosestProviders,
      appendPatternOfCareProviders: state.appendPatternOfCareProviders,
      filename: state.filename,
      includeActive: state.includeActive,
      includeApproved: state.includeApproved,
      includeFacilitySpecialties: state.includeFacilitySpecialties,
      includeOpportunity: state.includeOpportunity,
      includePending: state.includePending,
      includePhysicianSpecialties: state.includePhysicianSpecialties,
      providerFilter: state.providerFilter,
      reportFormat: state.reportFormat,
      selectedCounties: state.selectedCounties,
      selectedSpecialties: state.selectedSpecialties,
      selectedStates: state.selectedStates,
      serviceAreaGapsType: state.serviceAreaGapsType,
      specialtyGapsType: state.specialtyGapsType,
    };
  }

  const getChangeProviderSourceSideEffects = (newProviderSource: ProviderNetworkTypeEnum): ChangeProviderSourceSideEffects => {
    const isProviderReportType = state.reportType === ReportType.AdequacyServicingProvider || state.reportType === ReportType.ZipAnalysisAdequacyServicingProvider;
    const newFilename = isProviderReportType ? getDefaultReportName(startCase(newProviderSource)) : state.filename;
    let newProviderFilter = undefined as NetworkProviderFilterType | ExportProviderAvailabilityType;
    let newReportFormat = state.reportFormat;
    if (newProviderSource === ProviderNetworkTypeEnum.Network) {
      newProviderFilter = NetworkProviderFilterType.Available;
    } else if (newProviderSource === ProviderNetworkTypeEnum.Prospect || newProviderSource === ProviderNetworkTypeEnum.Market) {
      newProviderFilter = ExportProviderAvailabilityType.All;
    }

    if (state.reportFormat === ReportFormatType.DISTINCT_SERVICING_PROVIDER_REPORT
      && newProviderSource !== ProviderNetworkTypeEnum.Network
      && newProviderSource !== ProviderNetworkTypeEnum.Prospect
      && newProviderSource !== ProviderNetworkTypeEnum.Market) {
      newReportFormat = ReportFormatType.Default;
    }

    const resets = newProviderSource === ProviderNetworkTypeEnum.NetNew ? {
      ...getServiceAreaSectionDefaults(),
      ...getSpecialtySectionDefaults(),
    } : {
      includeActive: state.includeActive,
      includeApproved: state.includeApproved,
      includeFacilitySpecialties: state.includeFacilitySpecialties,
      includeOpportunity: state.includeOpportunity,
      includePending: state.includePending,
      includePhysicianSpecialties: state.includePhysicianSpecialties,
      selectedCounties: state.selectedCounties,
      selectedSpecialties: state.selectedSpecialties,
      selectedStates: state.selectedStates,
      serviceAreaGapsType: state.serviceAreaGapsType,
      specialtyGapsType: state.specialtyGapsType,
    };


    return {
      ...resets,
      appendNextClosestProviders: false,
      appendPatternOfCareProviders: false,
      filename: newFilename,
      providerFilter: newProviderFilter,
      reportFormat: newReportFormat,
    }
  }

  const changeReportFormat = (newReportFormat: ReportFormatType) => {
    if (enabledFeatureFlags.has(Features.RPPO_NON_PAR_PROVIDER_TO_HSD_TABLE_INCLUSION)
      && newReportFormat === ReportFormatType.MA
      && (state.providerSource === ProviderNetworkTypeEnum.Prospect || state.providerSource === ProviderNetworkTypeEnum.Market)
      && findTimeslice(state.timeslice)?.rppoNonContractedProvidersIncludedInHsd) {
      changeProviderSource(ProviderNetworkTypeEnum.Network);
    }
    const changeReportFormatSideEffects = getChangeReportFormatSideEffects(newReportFormat);

    dispatch({
      payload: {
        ...changeReportFormatSideEffects,
        reportFormat: newReportFormat,
      },
      type: ReportCustomizationReducerActions.ChangeReportFormat,
    });
    if (state.appendPatternOfCareProviders && !changeReportFormatSideEffects.appendPatternOfCareProviders) {
      renderPocUncheckedToast();
    }
  }

  const getDummyChangeReportFormatSideEffects = (): ChangeReportFormatSideEffects => {
    return {
      appendPatternOfCareProviders: state.appendPatternOfCareProviders,
      selectedPreset: state.selectedPreset,
    };
  };

  const getChangeReportFormatSideEffects = (newReportFormat: ReportFormatType): ChangeReportFormatSideEffects => {
    let newPatternOfCare = state.appendPatternOfCareProviders;
    let newSelectedPreset = state.selectedPreset;
    // assuming that if appendPatternOfCareProviders is true, the user has the proper roles and such
    if (state.appendPatternOfCareProviders
      && newReportFormat !== ReportFormatType.MA && newReportFormat !== ReportFormatType.MMP) {
      newPatternOfCare = false;
    }

    if (newReportFormat === ReportFormatType.MA || newReportFormat === ReportFormatType.MMP) {
      newSelectedPreset = defaultValues.selectedPreset;
    }
    return {
      appendPatternOfCareProviders: newPatternOfCare,
      selectedPreset: newSelectedPreset,
    };
  };

  // if the changed gaps filter changes
  const changeChangedGapsType = (newChangedGaps: ChangedGapsEnum | "") => {
    // the reducer is expecting values for these either way, so something must be provided
    const resets = newChangedGaps === "" ? {
      includeActive: state.includeActive,
      includeApproved: state.includeApproved,
      includeFacilitySpecialties: state.includeFacilitySpecialties,
      includeOpportunity: state.includeOpportunity,
      includePending: state.includePending,
      includePhysicianSpecialties: state.includePhysicianSpecialties,
      selectedCounties: state.selectedCounties,
      selectedSpecialties: state.selectedSpecialties,
      selectedStates: state.selectedStates,
      serviceAreaGapsType: state.serviceAreaGapsType,
      specialtyGapsType: state.specialtyGapsType,
    } : {
      ...getServiceAreaSectionDefaults(),
      ...getSpecialtySectionDefaults(),
      // If the defaults are something other than All, the report will be 
      // unhelpfully filtered to less than the changed gaps
      serviceAreaGapsType: ExportGapFilterType.All,
      specialtyGapsType: ExportGapFilterType.All,
    };

    dispatch({
      payload: {
        ...resets,
        changedGapsType: newChangedGaps,
      },
      type: ReportCustomizationReducerActions.ChangeChangedGapsFilter,
    });
  }

  const getReportType = (newReportType?: ReportType) => {

    const reportType = newReportType ? newReportType : state.reportType;

    if (state.isCompareMode || state.isDualMembership) {
      let newReportType;

      switch (reportType) {
        case ReportType.AdequacyServicingProvider:
          newReportType = ReportType.AdequacyServicingProviderComparison;
          break;
        case ReportType.AdequacyAggregate:
          newReportType = ReportType.AdequacyAggregateComparison;
          break;
        case ReportType.ZipSummary:
          newReportType = ReportType.ZipSummaryComparison;
          break;
        case ReportType.AdequacySpecialtySummary:
          newReportType = ReportType.AdequacySpecialtySummaryComparison;
          break;
        case ReportType.StateSummary:
          newReportType = ReportType.StateSummaryComparison;
          break;
        case ReportType.CountySummary:
          newReportType = ReportType.CountySummaryComparison;
          break;
        case ReportType.RegionSummary:
          newReportType = ReportType.RegionSummaryComparison;
          break;
        case ReportType.SpecialtySummaryByCounty:
          newReportType = ReportType.SpecialtySummaryByCountyComparison;
          break;
        case ReportType.ZipAnalysisAdequacyServicingProvider:
          newReportType = ReportType.ZipAnalysisAdequacyServicingProviderComparison;
          break;
        case ReportType.ZipClassAggregateResultsReport:
          newReportType = ReportType.ZipClassAggregateResultsReportComparison;
          break;
        case ReportType.ZipAnalysisResultsReport:
          newReportType = ReportType.ZipAnalysisResultsReportComparison;
          break;
        default:
          newReportType = state.reportType;
          break;
      }
      return newReportType;
    }
    else {
      return reportType;
    }
  }

  const renderPocUncheckedToast = () => {
    toast("\"Append Pattern of Care Providers\" was unchecked", {
      containerId: "z-index-1030",
      type: "info",
    });
  }

  const getNewFilename = (newReportType?: ReportType, newIsCompareMode?: boolean, newMemberships?: DropdownItem<number>[]) => {
    let prefix = "";
    const { isCompareMode, selectedMemberships, providerSource, reportType } = state;
    const isComparison = newIsCompareMode === true || (newIsCompareMode === undefined && isCompareMode)
      || newMemberships?.length === 2 || (newMemberships === undefined && selectedMemberships.length === 2);
    if (newReportType === ReportType.AdequacyServicingProvider || newReportType === ReportType.ZipAnalysisAdequacyServicingProvider) {
      prefix = startCase(providerSource);
      if (isComparison) {
        prefix += " Comparison";
      }
    } else {
      prefix = getDefaultReportNamePrefixByReportType(newReportType || reportType, isComparison);
    }
    const newFilename = getDefaultReportName(prefix);
    return newFilename;
  }

  // API Requests
  useEffect(() => {
    const promises: Promise<unknown>[] = [
      requestTimeslices(),
      requestSpecialtyGroups(),
      requestPresets(getReportType()),
      requestNetNewStatus(),
    ];

    if (state.reportType === ReportType.StateSummary) {
      promises.push(requestStates());
    } else if (!routeState.isZipProject) {
      promises.push(requestCounties());
    } else {
      promises.push(requestZipCounties());
    }

    Promise.all(promises).then(([timeslicesResponse, specialtiesResponse, presetsResponse, netNewResponse, serviceAreaResponse]) => {
      const { combinedServiceAreas, newSelectedServiceAreas } = serviceAreaResponse as { combinedServiceAreas: (ServiceAreaNestingParentDropdownItem | DropdownItem<string>)[], newSelectedServiceAreas: string[] };
      const { combinedSpecialties, newSelectedSpecialties } = specialtiesResponse as { combinedSpecialties: DropdownItem<string>[], newSelectedSpecialties: DropdownItem<string>[] };
      const isStateSummary = state.reportType === ReportType.StateSummary;
      const isAllSpecialties = state.reportType === ReportType.AdequacyAggregate;

      // timeslicesResponse only returns primary membership timeslices.
      // for non-dual membership timeslices, companionProjectRunConfigurationId defaults to 0
      const { monthly, all } = timeslicesResponse as ReportCustomizationRunConfigurationApiResponse;
      const defaultRunConfiguration = findTimeslice(state.timeslice, state.runFrequency, all, monthly);
      const newTimeslice = defaultRunConfiguration?.projectRunConfigurationId.toString();
      monthly.forEach((m) => {
        m.monthYear = new Date(m.dateCreated).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      })

      const payload: LoadPageAction = {
        allServiceAreaCounties: !isStateSummary || isAllSpecialties ? combinedServiceAreas as ServiceAreaNestingParentDropdownItem[] : state.allServiceAreaCounties,
        allServiceAreaStates: isStateSummary || isAllSpecialties ? combinedServiceAreas : state.allServiceAreaStates,
        allSpecialties: combinedSpecialties,
        allTimeslices: timeslicesResponse as ReportCustomizationRunConfigurationApiResponse,
        presets: presetsResponse as ColumnConfigurationPresetOption[],
        selectedCounties: newSelectedServiceAreas,
        selectedSpecialties: newSelectedSpecialties,
        selectedStates: state.reportType === ReportType.StateSummary ? newSelectedServiceAreas : [],
        selectedZipClass: state.selectedZipClass,
        showNetNewProviders: netNewResponse as boolean,
        timeslice: newTimeslice,
      };

      defaultValues.showNetNewProviders = payload.showNetNewProviders;
      defaultValues.selectedZipClass = payload.selectedZipClass;

      dispatch({
        payload,
        type: ReportCustomizationReducerActions.LoadPage,
      });
    })
  }, []);

  const requestTimeslices = async (): Promise<ReportCustomizationRunConfigurationApiResponse> => {
    let { monthly, all } = await projectService.getTimeslicesByProjectId(projectId.toString());
    if (routeState.isZipProject) {
      const zipTargetingEnabled = enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TARGETING);
      const zipTerminationEnabled = enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_TERMINATION);
      const filterProjectedTermed = (timeslices: typeof all) => timeslices.filter((t) => {
        if (t.isProjected && !zipTargetingEnabled) return false;
        if (t.isTermed && !zipTerminationEnabled) return false;
        if (t.isProjectedTermed && (!zipTargetingEnabled || !zipTerminationEnabled)) return false;
        return true;
      });
      monthly = filterProjectedTermed(monthly);
      all = filterProjectedTermed(all);
    }
    monthly = monthly.map((timeslice) => ({ ...timeslice, dateCreated: formatDateTime(timeslice.dateCreated) }));
    all = all.map((timeslice) => ({ ...timeslice, dateCreated: formatDateTime(timeslice.dateCreated) }));
    return { all, monthly };
  }

  /**
   * Retrieves the list of counties in the run configuration's service area. This is used for adequacy county projects.
   */
  const requestCounties = async (): Promise<{ combinedServiceAreas: ServiceAreaNestingParentDropdownItem[], newSelectedServiceAreas: string[] }> => {
    if (state.allServiceAreaCounties.length > 0) {
      return { combinedServiceAreas: state.allServiceAreaCounties, newSelectedServiceAreas: state.selectedCounties };
    }
    const { comparedTimeslice, isCompareMode, timeslice } = state;
    const promises = [projectService.getCountiesByRunConfigurationId(routeMatch.params.projectId, timeslice, false, true)];
    if (isCompareMode && !!comparedTimeslice) {
      promises.push(projectService.getCountiesByRunConfigurationId(routeMatch.params.projectId, comparedTimeslice, false, true));
    }

    const [primaryServiceArea, secondaryServiceArea] = await Promise.all(promises);
    const combinedServiceAreas = intersectCounties(primaryServiceArea, secondaryServiceArea);
    const stateFilters = routeState.filters?.filter((f) => f.filterType === FilterType.State) ?? [];
    const countyFilter = routeState.filters?.find((f) => f.filterType === FilterType.County);

    let newSelectedServiceAreas: string[] = [];
    // this is only called on page load or when changing to not-state-summary, so the assumption is the default report type expects county region set ids
    if (routeState.selectedRegionSetIds?.length > 0) {
      const allCountyIds = combinedServiceAreas.flatMap((sa) => sa.children.map((c) => c.key));
      const validIds = routeState.selectedRegionSetIds.map((rsi) => rsi.toString()).filter((id) => allCountyIds.includes(id));
      newSelectedServiceAreas = validIds.length > 0 ? validIds : allCountyIds;
    } else if (!!countyFilter) {
      const allCounties = combinedServiceAreas.flatMap((csa) => csa.children);
      const selectedCounty = allCounties.find((county) => county.countySSA === countyFilter.filterValue);
      if (selectedCounty) {
        newSelectedServiceAreas = [selectedCounty.key];
      } else {
        newSelectedServiceAreas = allCounties.map((c) => c.key);
      }
    } else if (stateFilters.length > 0) {
      newSelectedServiceAreas = stateFilters.flatMap((sf) =>
        combinedServiceAreas.find((csa) => csa.name === sf.filterValue)?.children.map((c) => c.key) ?? []);
    } else {
      const allIds = (combinedServiceAreas).flatMap((sa) => sa.children.map((c) => c.key));
      newSelectedServiceAreas = allIds;
    }
    defaultValues.selectedCounties = newSelectedServiceAreas;
    countiesMap.current.set(timeslice, primaryServiceArea);
    countiesMap.current.set(comparedTimeslice, secondaryServiceArea);
    return { combinedServiceAreas, newSelectedServiceAreas };
  }

  /**
   * Retrieves the list of primary counties for all the zip codes in a project, which may differ from the run config's service area.
   * This is used for adequacy zip projects.
   */
  const requestZipCounties = async (): Promise<{ combinedServiceAreas: ServiceAreaNestingParentDropdownItem[], newSelectedServiceAreas: string[] }> => {
    if (state.allServiceAreaCounties.length > 0) {
      return { combinedServiceAreas: state.allServiceAreaCounties, newSelectedServiceAreas: state.selectedCounties };
    }
    const { comparedTimeslice, isCompareMode, timeslice } = state;

    const primaryServiceArea = (await zipProjectService.getZipCodeFilterOptions(projectId, parseInt(timeslice), true)).countiesAndStates;
    let secondaryServiceArea: ServiceAreaNestingParentDropdownItem[] = [];
    if (isCompareMode) {
      secondaryServiceArea = (await zipProjectService.getZipCodeFilterOptions(projectId, parseInt(comparedTimeslice), true)).countiesAndStates;
    }
    const combinedServiceAreas = intersectCounties(primaryServiceArea, secondaryServiceArea);

    let newSelectedServiceAreas: string[] = [];
    const filters = routeState.zipProjectSearchParameters as ZipCodeAnalysisSearchParameters;
    if (!!filters.state && !!filters.county) {
      // if we have a state and a county, get that one particular county
      const stateMatch = combinedServiceAreas.find((csa) => csa.key === filters.state);
      const countyMatch = (stateMatch as ServiceAreaNestingParentDropdownItem).children.find((c) => c.name === filters.county);
      if (countyMatch) {
        newSelectedServiceAreas = [countyMatch.key];
      }
    } else if (!!filters.state && !filters.county) {
      // if we have a state and no county, get the whole state
      newSelectedServiceAreas = (combinedServiceAreas as ServiceAreaNestingParentDropdownItem[]).find((s) => s.key === filters.state).children.map((c) => c.key);
    } else if (!filters.state && !!filters.county) {
      // if we have a county name and no state, get all counties with that name
      const counties = (combinedServiceAreas as ServiceAreaNestingParentDropdownItem[]).flatMap((s) => s.children.find((c) => c.name === filters.county)?.key).filter(c => !!c);
      if (counties?.length > 0) {
        newSelectedServiceAreas = counties;
      }
    }
    else if (!filters.state && !filters.county) {
      // if we have neither, get and select all counties
      const allCounties = (combinedServiceAreas as ServiceAreaNestingParentDropdownItem[]).flatMap((csa) => csa.children.map((c) => c.key));
      newSelectedServiceAreas = allCounties;
    }

    defaultValues.selectedCounties = newSelectedServiceAreas;
    countiesMap.current.set(timeslice, primaryServiceArea);
    countiesMap.current.set(comparedTimeslice, secondaryServiceArea);
    return { combinedServiceAreas, newSelectedServiceAreas };
  }

  const requestStates = async (): Promise<{ combinedServiceAreas: DropdownItem<string>[], newSelectedServiceAreas: string[] }> => {
    if (state.allServiceAreaStates.length > 0) {
      return { combinedServiceAreas: state.allServiceAreaStates, newSelectedServiceAreas: state.selectedStates };
    }
    const { comparedTimeslice, isCompareMode, timeslice } = state;
    const promises = [projectService.getStatesByRunConfigurationId(routeMatch.params.projectId, timeslice)];
    if (isCompareMode && !!comparedTimeslice) {
      promises.push(projectService.getStatesByRunConfigurationId(routeMatch.params.projectId, comparedTimeslice));
    }

    const [primaryServiceArea, secondaryServiceArea] = await Promise.all(promises);
    const combinedServiceAreas = intersectStates(primaryServiceArea, secondaryServiceArea);
    const stateFilters = routeState.filters?.filter((f) => f.filterType === FilterType.State) ?? [];
    let newSelectedServiceAreas: string[] = [];
    if (stateFilters.length > 0) {
      // the stateFilter is an abbreviation (e.g. IL) but we want to save the key/region set id (e.g. 1219)
      newSelectedServiceAreas = stateFilters
        .map((sf) => combinedServiceAreas.find((csa) => csa.name === sf.filterValue)?.key)
        .filter((key): key is string => key != null);
    }
    // this is only called on page load or when changing to state summary, so the assumption is the default report type expects state region set ids
    else if (routeState.selectedRegionSetIds?.length > 0) {
      newSelectedServiceAreas = routeState.selectedRegionSetIds.map((rsi) => rsi.toString());
    } else {
      const allIds = combinedServiceAreas.map((sa) => sa.key);
      newSelectedServiceAreas = allIds;
    }
    defaultValues.selectedStates = newSelectedServiceAreas;
    statesMap.current.set(timeslice, primaryServiceArea);
    statesMap.current.set(comparedTimeslice, secondaryServiceArea);
    return { combinedServiceAreas, newSelectedServiceAreas };
  }

  const requestSpecialtyGroups = async (): Promise<{ combinedSpecialties: DropdownItem<string>[], newSelectedSpecialties: DropdownItem<string>[] }> => {
    const { comparedTimeslice, isCompareMode, timeslice } = state;
    const promises = [projectService.getSpecialtiesByRunConfigurationId(routeMatch.params.projectId, timeslice)];
    if (isCompareMode && !!comparedTimeslice) {
      promises.push(projectService.getSpecialtiesByRunConfigurationId(routeMatch.params.projectId, comparedTimeslice));
    }
    const [primarySpecialties, secondarySpecialties] = await Promise.all(promises);
    const combinedSpecialties = intersectSpecialties(primarySpecialties, secondarySpecialties);
    const specialtyFilters = routeState.filters?.filter((f) => f.filterType === FilterType.Specialty) ?? [];
    let newSelectedSpecialties: DropdownItem<string>[] = [];
    // if it's a zip project, base the default selections on the specialty group code parameter instead of the specialty filter
    if (!!routeState.zipProjectSearchParameters?.specialtyGroupCode) {
      const { specialtyGroupCode } = routeState.zipProjectSearchParameters as ZipClassAnalysisSearchParameters;

      if (!!specialtyGroupCode) {
        const matchingSpecialty = combinedSpecialties.find((cs) => cs.key === specialtyGroupCode);
        newSelectedSpecialties = [matchingSpecialty];
      }
    } else {
      if (specialtyFilters.length > 0) {
        newSelectedSpecialties = specialtyFilters
          .map((sf) => combinedSpecialties.find((cs) => cs.key === sf.filterValue))
          .filter((s): s is DropdownItem<string> => s != null);
      } else if (routeState.selectedSpecialties?.length > 0) {
        const matches = combinedSpecialties.filter((ns) => routeState.selectedSpecialties.includes(ns.key));
        newSelectedSpecialties = matches;
      } else if (routeState.defaultReportType === ReportType.AdequacyServicingProvider && routeState.defaultProviderSource !== "netNew") {
        const matches = combinedSpecialties.filter((ns) => routeState.filters?.find(sf => sf.filterType === '/specialty/')?.filterValue.includes(ns.key));
        newSelectedSpecialties = matches.length > 0 ? matches : combinedSpecialties;
      } else {
        newSelectedSpecialties = combinedSpecialties;
      }
    }

    defaultValues.selectedSpecialties = newSelectedSpecialties;
    specialtiesMap.current.set(timeslice, primarySpecialties);
    specialtiesMap.current.set(comparedTimeslice, secondarySpecialties);
    return { combinedSpecialties, newSelectedSpecialties };
  }

  const requestPresets = async (reportType: ReportType = state.reportType): Promise<ColumnConfigurationPresetOption[]> => {
    try {
      const apiPresets = await exportService.getColumnCustomizationPresets(reportType);
      return [defaultValues.selectedPreset, ...apiPresets];
    } catch {
      return [defaultValues.selectedPreset];
    }
  }

  const requestNetNewStatus = async () => {
    try {
      if (user.roles.includes(Roles.NET_NEW_PROVIDER_USER)) {
        return await netNewServicingProviderService.projectHasNetNewServicingProviders(parseInt(routeMatch.params.projectId, 10));
      }
    } catch {
      return false;
    }
  }

  const changeLoadedPresets = async (newReportType?: ReportType) => {
    const updatedPresets = await requestPresets(getReportType(newReportType));
    dispatch({
      payload: { presets: updatedPresets },
      type: ReportCustomizationReducerActions.ChangeLoadedPresets,
    });
  };

  const changeSelectedPreset = (newSelectedPreset: ColumnConfigurationPresetOption) => {
    dispatch({
      payload: { selectedPreset: newSelectedPreset },
      type: ReportCustomizationReducerActions.ChangeSelectedPreset,
    });
  };

  const intersectStates = (primaryServiceArea: DropdownItem<string>[], secondaryServiceArea: DropdownItem<string>[] = []) => {
    const combinedServiceAreas = primaryServiceArea;
    if (secondaryServiceArea?.length > 0) {
      secondaryServiceArea.forEach((sa) => {
        if (!combinedServiceAreas.find((ca) => ca.key === sa.key)) {
          combinedServiceAreas.push(sa);
        }
      })
    }
    return combinedServiceAreas.sort((a, b) => a.name > b.name ? 1 : -1);
  }

  const intersectCounties = (primaryServiceArea: ServiceAreaNestingParentDropdownItem[], secondaryServiceArea: ServiceAreaNestingParentDropdownItem[] = []) => {
    // see which states are in both timeslices, and then check the counties
    return (secondaryServiceArea.length > 0
      ? primaryServiceArea.reduce((result, current) => {
        const secondaryStateMatch = secondaryServiceArea.find((s) => s.key === current.key);
        if (!!secondaryStateMatch) {
          const newState = current;
          newState.children = current.children.filter((c) => !!secondaryStateMatch.children.find((s) => s.key === c.key));
          result.push(newState);
        }
        return result;
      }, new Array<ServiceAreaNestingParentDropdownItem>())
      : primaryServiceArea)
      .sort((a, b) => a.name > b.name ? 1 : -1)
      .map((state) => { state.children.sort((a, b) => a.name > b.name ? 1 : -1); return state; });
  }

  const resetCompareMode = async (prevReportType: ReportType): Promise<ResetCompareModeSideEffects> => {
    let newIsCompareMode = state.isCompareMode;
    let newComparedTimeslice = state.comparedTimeslice;
    let changeIsCompareModeSideEffects = getDummyChangeIsCompareModeSideEffects();
    let changeTimesliceSideEffects = getDummyChangeTimesliceSideEffects();

    // ClosestProvider does not allow compare mode
    if (prevReportType === ReportType.ClosestProvidersToMembers) {
      newIsCompareMode = defaultValues.isCompareMode;
      changeIsCompareModeSideEffects = getChangeIsCompareModeSideEffects(newIsCompareMode);
      newComparedTimeslice = defaultValues.comparedTimeslice === state.timeslice
        ? state.allTimeslices[state.runFrequency].find((t) => t.projectRunConfigurationId.toString() !== state.timeslice).projectRunConfigurationId.toString()
        : defaultValues.comparedTimeslice;

      changeTimesliceSideEffects = await getChangeTimesliceSideEffects(state.timeslice, newComparedTimeslice);
    }
    return {
      ...changeIsCompareModeSideEffects,
      ...changeTimesliceSideEffects,
      comparedTimeslice: newComparedTimeslice,
      isCompareMode: newIsCompareMode,
    };
  }

  const getServiceAreaSectionDefaults = (): ServiceAreaSectionDefaults => {
    return {
      excludeZeroMembership: defaultValues.excludeZeroMembership,
      includeActive: defaultValues.includeActive,
      includeApproved: defaultValues.includeApproved,
      includeOpportunity: defaultValues.includeOpportunity,
      includePending: defaultValues.includePending,
      networkAssessmentAnalysisTypes: defaultValues.networkAssessmentAnalysisTypes,
      selectedCounties: defaultValues.selectedCounties,
      selectedStates: defaultValues.selectedStates,
      selectedZipClass: defaultValues.selectedZipClass,
      serviceAreaGapsType: defaultValues.serviceAreaGapsType,
    };
  }

  const getSpecialtySectionDefaults = (): SpecialtySectionDefaults => {
    return {
      includeFacilitySpecialties: defaultValues.includeFacilitySpecialties,
      includePhysicianSpecialties: defaultValues.includePhysicianSpecialties,
      selectedSpecialties: defaultValues.selectedSpecialties,
      specialtyGapsType: defaultValues.specialtyGapsType,
    };
  }

  // Determines the selected counties for the Adequacy Aggregate report based on the previous report type and the new service area selections
  const getSelectedCountiesForAdequacyAggregate = (
    prevReportType: ReportType,
    newServiceArea: ServiceAreaNestingParentDropdownItem[],
    newSelectedServiceAreas: string[]
  ): string[] => {
    let isComingFromStateSummary = false;
    let isAllStateSelected = false;
    let shouldRetainCountySelections = false;

    if (prevReportType === ReportType.StateSummary) {
      isComingFromStateSummary = true;

      isAllStateSelected = state.allServiceAreaStates.length > 0
        && state.selectedStates.length === state.allServiceAreaStates.length
        && state.allServiceAreaStates.every(s => state.selectedStates.includes(s.key));
    } else {
      const allCountyIds = newServiceArea.flatMap((csa) => csa.children.map((c) => c.key));
      const isAllCountiesSelected = state.selectedCounties.length === allCountyIds.length
        && state.selectedCounties.every(selected => allCountyIds.includes(selected));
      // Adequacy Aggregate (All specialties) report only allow one county or all counties selection
      shouldRetainCountySelections = isAllCountiesSelected || state.selectedCounties.length === 1;
    }

    if (isComingFromStateSummary) {
      // If coming from StateSummary with all states selected, select all counties, otherwise clear the selection
      if (isAllStateSelected) {
        return newServiceArea.flatMap((csa) => csa.children.map((c) => c.key));
      } else {
        return [];
      }
    } else {
      return shouldRetainCountySelections ? newSelectedServiceAreas : [];
    }
  }

  const getProviderSectionDefaults = (): ProviderSectionDefaults => {
    return {
      ...getChangeProviderSourceSideEffects(defaultValues.providerSource),
      appendNextClosestProviders: defaultValues.appendNextClosestProviders,
      appendPatternOfCareProviders: defaultValues.appendPatternOfCareProviders,
      providerFilter: defaultValues.providerFilter,
      providerSource: defaultValues.providerSource,
      showAddedProviders: defaultValues.showAddedProviders,
      showMatchedProviders: defaultValues.showMatchedProviders,
      showRemovedProviders: defaultValues.showRemovedProviders,
    };
  }

  const validateSettings = () => {
    if (state.reportType === ReportType.AdequacyAggregate && state.selectedCounties.length === 0) {
      return false;
    }
    // a Termed/Projected/Projected-Termed timeslice can be pre-selected via route state from outside this wizard's own
    // dropdown (which already disables it correctly), so Generate must also re-check disabled state directly
    return state.selectedMemberships.length > 0
      && !!state.timeslice && (!state.isCompareMode || (state.isCompareMode && !!state.comparedTimeslice))
      && findTimeslice(state.timeslice)?.isDisabled !== true
      && (!state.isCompareMode || findTimeslice(state.comparedTimeslice)?.isDisabled !== true)
      && !!state.filename
      && (state.includeActive || state.includePending || state.includeApproved || state.includeOpportunity);
  }

  const getSelectedRunConfigurationIdsInChronologicalOrder = (): { primaryId: number, secondaryId: number } => {
    if (state.allTimeslices.all.length === 0) {
      return { primaryId: undefined, secondaryId: undefined };
    }
    const primaryConfig = findTimeslice(state.timeslice);
    const secondaryConfig = findTimeslice(state.comparedTimeslice) ?? {} as Partial<ReportCustomizationRunConfiguration>;
    const projectionNames = [ReportCustomizationRunConfigurationNamesEnum.Projected, ReportCustomizationRunConfigurationNamesEnum.Termed, ReportCustomizationRunConfigurationNamesEnum.ProjectedTermed];
    const isOnlySecondaryMembershipSelected = state.selectedMemberships.some(item => item.key === primaryConfig.secondaryMemberSetId) && !state.isDualMembership;
    const runConfigKey: keyof ReportCustomizationRunConfiguration = isOnlySecondaryMembershipSelected ? "companionProjectRunConfigurationId" : "projectRunConfigurationId";

    // if we're in dual membership, return the primary and secondary membership configurations in order
    if (state.isDualMembership) {
      return {
        primaryId: primaryConfig.projectRunConfigurationId,
        secondaryId: primaryConfig.companionProjectRunConfigurationId
      };
    }

    // note that projected timeslices will have the same dateCreated as the latest published, so < and > will be false
    if (
      // if there's no secondary
      !secondaryConfig?.dateCreated
      // or the secondary is projected and the primary isn't
      || (!projectionNames.includes(primaryConfig.name) && projectionNames.includes(secondaryConfig.name))
      // or the secondary is newer
      || new Date(secondaryConfig.dateCreated) > new Date(primaryConfig.dateCreated)
    ) {
      if (secondaryConfig.secondaryMemberSetId === 0) {
        // handle edge case where dual membership was added to the project after the first few runs
        // Or secondary membership is deleted but timeslice still exists 
        return { primaryId: primaryConfig[runConfigKey], secondaryId: secondaryConfig.projectRunConfigurationId };
      }
      // return them as-is
      return { primaryId: primaryConfig[runConfigKey], secondaryId: secondaryConfig[runConfigKey] };
    }

    if (
      // but if the primary is newer
      new Date(primaryConfig.dateCreated) > new Date(secondaryConfig.dateCreated)
      // or the primary is projected and the secondary isn't
      || (projectionNames.includes(primaryConfig.name) && !projectionNames.includes(secondaryConfig.name))
    ) {
      // switch them
      if (secondaryConfig.secondaryMemberSetId === 0) {
        // handle edge case where dual membership was added to the project after the first few runs
        // Or secondary membership is deleted but timeslice still exists 
        return { primaryId: secondaryConfig.projectRunConfigurationId, secondaryId: primaryConfig[runConfigKey] };
      }
      return { primaryId: secondaryConfig[runConfigKey], secondaryId: primaryConfig[runConfigKey] };
    }

    // otherwise we have two projected timeslices and we need to order those
    const projectionNameOrderMap = new Map<ReportCustomizationRunConfigurationNamesEnum, number>([
      [ReportCustomizationRunConfigurationNamesEnum.Projected, 1],
      [ReportCustomizationRunConfigurationNamesEnum.Termed, 2],
      [ReportCustomizationRunConfigurationNamesEnum.ProjectedTermed, 3],
    ]);
    if (projectionNameOrderMap.get(primaryConfig.name) > projectionNameOrderMap.get(secondaryConfig.name)) {
      return { primaryId: secondaryConfig[runConfigKey], secondaryId: primaryConfig[runConfigKey] };
    } else {
      return { primaryId: primaryConfig[runConfigKey], secondaryId: secondaryConfig[runConfigKey] };
    }
  }

  return (
    <ReportCustomizationContext.Provider
      value={{
        changeChangedGapsType,
        changeFileFormat,
        changeIsCompareMode,
        changeLoadedPresets,
        changeMemberships,
        changeProviderSource,
        changeReportFormat,
        changeReportType,
        changeRunFrequency,
        changeSelectedPreset,
        changeTimeslice,
        dispatch,
        excelRecordsLimit,
        findTimeslice,
        getReportType,
        getSelectedRunConfigurationIdsInChronologicalOrder,
        maxExcelRecords,
        nonRecordRows,
        routeParams: routeMatch.params,
        routeState,
        state,
        validateSettings,
      }}>
      {props.children}
    </ReportCustomizationContext.Provider>
  )
}

export default ReportCustomizationContextProvider;